import { Pencil } from 'lucide-react';
import * as React from 'react';
import { WEEKDAY_KEYS, formatDuration } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Combobox, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import { ContractDialog } from '@/components/admin/contract-dialog';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { ApiError } from '@/lib/api';
import { useAssignHolidayGroup, useContracts, useHolidayGroups } from '@/lib/admin-api';

const DAY_LABEL: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

/**
 * Every working contract on one screen.
 *
 * Contracts were editable one dialog at a time, which is fine for a correction and
 * useless for the two things anybody actually wants: seeing who has no contract at
 * all, and putting this year's holiday list on everybody. Both are questions about
 * the whole set, so the whole set is the screen.
 */
export function ContractsPage() {
  const [includeDisabled, setIncludeDisabled] = React.useState(false);
  const [editing, setEditing] = React.useState<{ id: string; displayName: string } | null>(null);
  const [groupId, setGroupId] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<React.ReactNode>(null);
  const [error, setError] = React.useState<string | null>(null);

  const { data, isPending } = useContracts(includeDisabled);
  const { data: groups } = useHolidayGroups();
  const assign = useAssignHolidayGroup();

  const rows = data ?? [];
  const without = rows.filter((row) => row.contract === null);

  const applyGroup = async () => {
    if (!groupId) return;
    setError(null);
    setNotice(null);
    try {
      const result = await assign.mutateAsync(groupId);
      setNotice(
        <>
          {result.group} is now on {result.updated} contract
          {result.updated === 1 ? '' : 's'}.
          {result.skipped.length > 0 && (
            <>
              {' '}
              {result.skipped.length} {result.skipped.length === 1 ? 'person has' : 'people have'} no
              contract, so there was nothing to put it on:{' '}
              <span className="font-medium">{result.skipped.join(', ')}</span>.
            </>
          )}
        </>,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not assign that group.');
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Working contracts"
        description="Who works which days, against which holiday calendar."
        actions={
          <Button hint={"Also show people who have been deactivated"} variant="outline" onClick={() => setIncludeDisabled((v) => !v)}>
            {includeDisabled ? 'Hide people who have left' : 'Include people who have left'}
          </Button>
        }
      />

      <Card>
        <CardContent className="space-y-3 py-5">
          <div>
            <h3 className="text-sm font-semibold">Put one holiday group on every contract</h3>
            <p className="mt-1 max-w-prose text-xs leading-relaxed text-muted-foreground">
              There is one holiday list and everybody follows it, so doing this a dialog
              at a time is how it ends up half-applied. Contracts that do not exist are
              left alone rather than created from defaults &mdash; a target nobody agreed
              to becomes overtime that reaches payroll.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-72">
              <Combobox
                value={groupId}
                onChange={setGroupId}
                options={(groups ?? []).map((group) => ({ value: group.id, label: group.name }))}
                placeholder="Choose a holiday group"
                searchPlaceholder="Search groups"
              />
            </div>
            <ConfirmButton
              title="Apply this holiday group to everybody?"
              description={`Every existing working contract will follow ${
                groups?.find((group) => group.id === groupId)?.name ?? 'this group'
              }. It replaces whatever group each contract has now, and there is no undo beyond setting the old one back.`}
              confirmLabel="Apply to all"
              disabled={!groupId || assign.isPending}
              onConfirm={() => void applyGroup()}
            >
              Assign to all
            </ConfirmButton>
          </div>

          {notice && (
            <Alert>
              <AlertDescription className="text-sm leading-relaxed">{notice}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Said out loud rather than left as blank cells further down. Somebody with
          no contract has no target, so they appear in no overtime figure and no
          missing-days list - which reads as "up to date" and is not. */}
      {without.length > 0 && (
        <Alert>
          <AlertDescription className="text-sm leading-relaxed">
            <strong>{without.length}</strong> of {rows.length} have no working contract, so
            nothing can be expected of them and they appear in no overtime or missing-days
            figure:{' '}
            <span className="font-medium">
              {without.map((row) => row.displayName).join(', ')}
            </span>
            .
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Week</TableHead>
                    <TableHead className="text-right">Leave</TableHead>
                    <TableHead>Holiday group</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Left</TableHead>
                    <TableHead className="w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} className={row.enabled ? undefined : 'ts-locked'}>
                      <TableCell>
                        <span className="font-medium">{row.displayName}</span>
                        <span className="block text-xs text-muted-foreground">{row.email}</span>
                      </TableCell>
                      <TableCell>
                        {row.contract ? (
                          <div className="flex flex-wrap gap-1">
                            {WEEKDAY_KEYS.map((key) => {
                              const seconds = row.contract!.hoursPerDay[key] ?? 0;
                              return (
                                <span
                                  key={key}
                                  className={`rounded px-1.5 py-0.5 text-[11px] ${
                                    seconds > 0
                                      ? 'bg-muted font-medium'
                                      : 'text-muted-foreground opacity-60'
                                  }`}
                                >
                                  {DAY_LABEL[key]} {seconds > 0 ? formatDuration(seconds) : '—'}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <Badge variant="outline">no contract</Badge>
                        )}
                      </TableCell>
                      <TableCell className="ts-num text-right">
                        {row.contract ? `${row.contract.vacationDaysPerYear} days` : '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.contract?.holidayGroup?.name ?? (
                          <span className="text-muted-foreground">none</span>
                        )}
                      </TableCell>
                      <TableCell className="ts-num text-sm text-muted-foreground">
                        {row.contract?.workStartDate ?? '—'}
                      </TableCell>
                      <TableCell className="ts-num text-sm">
                        {row.contract?.workEndDate ? (
                          <span className="text-destructive">{row.contract.workEndDate}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button hint={"Change this person's working contract"}
                          variant="ghost"
                          size="sm"
                          aria-label={`Edit ${row.displayName}'s contract`}
                          onClick={() => setEditing({ id: row.id, displayName: row.displayName })}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                        Nobody to show.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* The same dialog the People screen uses - one contract editor, so the two
          screens cannot disagree about what a contract is. */}
      <ContractDialog employee={editing} onClose={() => setEditing(null)} />
    </PageContainer>
  );
}
